import type { PulseSentimentDirection } from '@terminal/views/theme-overview/tab-content/pulse-overview/pulse-types';

import { POSTHOG_TRACKING_EVENTS, PosthogTrackingSource } from '../constants';
import {
  CatalystDrawerOpenProperties,
  EarningsInsightDrawerOpenProperties,
  InsightDrawerOpenProperties,
  InsightIconClickedProperties,
  KgInsightPageOpenProperties,
  NavigateToKgInsightParams,
} from './insightsEventTypes';
import { CaptureTrackingEvent, GetEmailTrackingParams } from './types';

export const createInsightsEvents = (
  captureTrackingEvent: CaptureTrackingEvent,
  getEmailTrackingParams: GetEmailTrackingParams
) => {
  const trackInsightsOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.INSIGHTS_OPEN,
      properties: { source },
    });

  const trackMonthlySentimentDrawerOpen = (
    source: PosthogTrackingSource,
    direction: PulseSentimentDirection
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.MONTHLY_SENTIMENT_DRAWER_OPEN,
      properties: { source, direction },
    });

  const trackInsightsDrawerOpen = ({
    source,
    type,
    insightId,
    entities,
    themeIds,
    ...otherProperties
  }: InsightDrawerOpenProperties) => {
    const emailParams = getEmailTrackingParams();
    const finalSource = emailParams?.source ?? source;
    const finalOtherProperties = emailParams
      ? { ...otherProperties, utm_campaign: emailParams.utm_campaign }
      : otherProperties;

    return captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.INSIGHTS_DRAWER_OPEN,
      properties: {
        ...finalOtherProperties,
        source: finalSource,
        type,
        insightId,
        ...(entities !== undefined && { entities }),
        ...(themeIds !== undefined && { themeIds }),
      },
    });
  };

  const trackEarningsInsightDrawerOpen = ({
    source,
    type,
    insightId,
    entities,
    themeIds,
    ...otherProperties
  }: EarningsInsightDrawerOpenProperties) => {
    const emailParams = getEmailTrackingParams();
    const finalSource = emailParams?.source ?? source;
    const finalOtherProperties = emailParams
      ? { ...otherProperties, utm_campaign: emailParams.utm_campaign }
      : otherProperties;

    return captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.EARNINGS_INSIGHT_DRAWER_OPEN,
      properties: {
        ...finalOtherProperties,
        source: finalSource,
        type,
        insightId,
        ...(entities !== undefined && { entities }),
        ...(themeIds !== undefined && { themeIds }),
      },
    });
  };

  const trackCatalystDrawerOpen = ({
    source,
    type,
    insightId,
    entities,
    themeIds,
    ...otherProperties
  }: CatalystDrawerOpenProperties) => {
    const emailParams = getEmailTrackingParams();
    const finalSource = emailParams?.source ?? source;
    const finalOtherProperties = emailParams
      ? { ...otherProperties, utm_campaign: emailParams.utm_campaign }
      : otherProperties;

    return captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.CATALYST_DRAWER_OPEN,
      properties: {
        ...finalOtherProperties,
        source: finalSource,
        type,
        insightId,
        ...(entities !== undefined && { entities }),
        ...(themeIds !== undefined && { themeIds }),
      },
    });
  };

  const trackInsightIconClicked = ({
    source,
    type,
    insightId,
    entities,
    themeIds,
  }: InsightIconClickedProperties) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.INSIGHT_ICON_CLICKED,
      properties: {
        source,
        type,
        insightId,
        ...(entities !== undefined && { entities }),
        ...(themeIds !== undefined && { themeIds }),
      },
    });

  const trackKgInsightPageOpen = ({
    source,
    insightId,
    category,
  }: KgInsightPageOpenProperties) => {
    const emailParams = getEmailTrackingParams();
    const finalSource = emailParams?.source ?? source ?? null;

    return captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.KG_INSIGHT_PAGE_OPEN,
      properties: {
        ...emailParams,
        source: finalSource,
        insightId,
        category,
      },
    });
  };

  const trackKgInsightReadFullCatalyst = ({
    source,
    category,
    insightId,
  }: NavigateToKgInsightParams) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.KG_INSIGHT_READ_FULL_CATALYST,
      properties: {
        source,
        category,
        insightId,
      },
    });

  return {
    trackInsightsOpen,
    trackMonthlySentimentDrawerOpen,
    trackInsightsDrawerOpen,
    trackEarningsInsightDrawerOpen,
    trackCatalystDrawerOpen,
    trackInsightIconClicked,
    trackKgInsightPageOpen,
    trackKgInsightReadFullCatalyst,
  };
};
